import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import styled from "styled-components";

const enum fields {
    heading = "heading",
    cards = "cards",
    accentColor = "accentColor",
    textColor = "textColor",
    borderColor = "borderColor",
    ruleBorderColor = "ruleBorderColor",
    backgroundColor = "backgroundColor",
}

interface ReasonFields {
    number: string;
    title: string;
    description: string;
}

interface ReasonItem extends HasFields {
    fields: ReasonFields;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.cards]: ReasonItem[];
        [fields.accentColor]: string;
        [fields.textColor]: string;
        [fields.borderColor]: string;
        [fields.ruleBorderColor]: string;
        [fields.backgroundColor]: string;
    };
}

type Props = OwnProps;

const WhyChooseCooney: React.FC<Props> = ({ fields: content }) => {
    return (
        <Section>
            <Heading textColor={content.textColor}>{content.heading}</Heading>
            <Rule borderColor={content.ruleBorderColor} />
            <CardGrid>
                {(content.cards || []).map((card, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ReasonCard
                        key={index}
                        backgroundColor={content.backgroundColor}
                        borderColor={content.borderColor}
                    >
                        <CardTopLine>
                            <CardNumber accentColor={content.accentColor}>{card.fields.number}</CardNumber>
                            <CardDash borderColor={content.ruleBorderColor} />
                        </CardTopLine>
                        <CardTitle textColor={content.textColor}>{card.fields.title}</CardTitle>
                        <CardDescription textColor={content.textColor}>{card.fields.description}</CardDescription>
                    </ReasonCard>
                ))}
            </CardGrid>
        </Section>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.heading,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "WHY CHOOSE COONEY",
            fieldType: "General",
        },
        {
            name: fields.accentColor,
            displayName: "Accent Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#e63946",
            fieldType: "General",
        },
        {
            name: fields.textColor,
            displayName: "Text Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#3f4448",
            fieldType: "General",
        },
        {
            name: fields.borderColor,
            displayName: "Border Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#d9d9d9",
            fieldType: "General",
        },{
            name: fields.ruleBorderColor,
            displayName: "Rule Border Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#000000",
            fieldType: "General",
        },
        {
            name: fields.backgroundColor,
            displayName: "Card Background",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ffffff",
            fieldType: "General",
        },
        {
            name: fields.cards,
            displayName: "Reasons",
            editorTemplate: "ListField",
            defaultValue: [
                {
                    fields: {
                        number: "01",
                        title: "REAL PEOPLE, REAL HELP",
                        description:
                            "Call, chat, or email - you will always reach a real person who knows what they are talking about. We build relationships with our customers that last for years, not just one order.",
                    },
                },
                {
                    fields: {
                        number: "02",
                        title: "THIRD GENERATION EXPERTISE",
                        description:
                            "Three generations under one roof. The Cooney Brothers team offers over 85 years of product knowledge and problem-solving experience you will not find at a typical distributor.",
                    },
                },
                {
                    fields: {
                        number: "03",
                        title: "WE CAN SOURCE DARN-NEAR ANYTHING",
                        description:
                            "Got a part number, a picture, or just a description of a problem? Our sales reps will track it down, at a fair price and will get it to you when you need it.",
                    },
                },
                {
                    fields: {
                        number: "04",
                        title: "BRANDS YOU TRUST",
                        description:
                            "We are an authorized distributor for top manufacturers in the industry, so you know exactly what you are getting - no knockoffs, no guesswork.",
                    },
                },
            ],
            fieldType: "General",
            getDisplay: (item: HasFields) => item.fields.title || item.fields.number,
            fieldDefinitions: [
                {
                    name: "number",
                    displayName: "Number",
                    editorTemplate: "TextField",
                    defaultValue: "01",
                },
                {
                    name: "title",
                    displayName: "Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "description",
                    displayName: "Description",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        }
        
    ],
};

const widgetModule: WidgetModule = {
    component: WhyChooseCooney,
    definition,
};

export default widgetModule;

const Section = styled.section`
    width: 100%;
    padding: 0 clamp(12px, 2vw, 20px);
    box-sizing: border-box;
    font-family: Arial, sans-serif;
`;

const Heading = styled.h2<{ textColor: string }>`
    margin: 0;
    color: ${({ textColor }) => textColor || "#3f4448"};
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0;
`;

const Rule = styled.div<{ borderColor: string }>`
    width: 100%;
    height: 1px;
    margin: clamp(16px, 2vw, 20px) 0 clamp(24px, 3vw, 34px);
    background: ${({ borderColor }) => borderColor || "#d9d9d9"};
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-auto-rows: minmax(220px, auto);
    align-items: stretch;
    width: 100%;
    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const ReasonCard = styled.article<{ backgroundColor: string; borderColor: string }>`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    padding: clamp(20px, 2.2vw, 27px) clamp(10px, 1.2vw, 16px) clamp(22px, 2.2vw, 28px);
    box-sizing: border-box;
    background: ${({ backgroundColor }) => backgroundColor || "#fff"};
    border: 1px solid ${({ borderColor }) => borderColor || "#d9d9d9"};
    border-right: 0;
    &:last-child {
        border-right: 1px solid ${({ borderColor }) => borderColor || "#d9d9d9"};
    }
         @media (max-width: 560px) {
        border: 1px solid ${({ borderColor }) => borderColor || "#d9d9d9"};
    }
`;

const CardTopLine = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: clamp(26px, 3vw, 30px);
    margin-bottom: clamp(15px, 2vw, 19px);
`;

const CardNumber = styled.div<{ accentColor: string }>`
    color: ${({ accentColor }) => accentColor || "#e63946"};
    font-size: clamp(22px, 2.3vw, 26px);
    font-weight: 700;
    line-height: 1;
`;

const CardDash = styled.div<{ borderColor: string }>`
    width: clamp(18px, 4vw, 32px);
    height: 1px;
    margin-right: clamp(0px, 1.5vw, 16px);
    background: ${({ borderColor }) => borderColor || "#888"};
`;

const CardTitle = styled.h3<{ textColor: string }>`
    min-height: 42px;
    color: ${({ textColor }) => textColor || "#3f4448"};
    font-size: clamp(13px, 1.3vw, 15px);
    font-weight: 700;
    line-height: 1.45;
    overflow-wrap: anywhere;
`;

const CardDescription = styled.p<{ textColor: string }>`
    margin: 0;
    color: ${({ textColor }) => textColor || "#3f4448"};
    font-size: clamp(14px, 1.2vw, 14px);
    font-weight: 400;
    line-height: 1.45;
    overflow-wrap: anywhere;
`;
